import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowDown, faArrowUp } from '@fortawesome/free-solid-svg-icons';
import { Box, IconButton, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import ModalConfirmDelete from 'components/modal-confirm-delete';
import { ListingEntry } from 'graphql/listings/queries';
import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NewAssociationType, NewOrderPartyType } from 'types/order';
import useAssociation from '../hooks/useAssociation';
import { associationKey, formatRoleName, normalizeAssociations } from '../utils';
import PartyModalAssociations from './PartyModalAssociations';
import PartySection from './PartySection';

const useStyles = makeStyles((theme: any) => ({
  addButton: {
    color: theme.palette.text.secondary,
    padding: theme.spacing(0.75),
    '&:hover': {
      color: theme.palette.primary.main,
      backgroundColor: theme.palette.background.default,
    },
    '@media (hover: none) and (pointer: coarse)': {
      minWidth: theme.spacing(5.5),
      minHeight: theme.spacing(5.5),
    },
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 0),
    '& + &': {
      borderTop: `1px solid ${theme.palette.neutraldivider}`,
    },
  },
  rowInfo: {
    display: 'flex',
    alignItems: 'flex-start',
    flex: 1,
    gap: theme.spacing(1),
    minWidth: 0,
  },
  icon: {
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.25),
  },
  text: {
    minWidth: 0,
  },
  name: {
    color: theme.palette.primary.darker,
    overflowWrap: 'anywhere',
  },
  meta: {
    color: theme.palette.text.secondary,
  },
  deleteButton: {
    color: theme.palette.text.secondary,
    padding: theme.spacing(0.75),
    '&:hover': {
      color: theme.palette.error.main,
    },
    '@media (hover: none) and (pointer: coarse)': {
      minWidth: theme.spacing(5.5),
      minHeight: theme.spacing(5.5),
    },
  },
  empty: {
    color: theme.palette.text.secondary,
  },
}));

interface PartyAssociationsListProps {
  associations: NewAssociationType[] | null;
  listingEntries: ListingEntry[];
  handleFilterParties: (parties: NewOrderPartyType[]) => void;
  partySelected: NewOrderPartyType;
}

const PartyAssociationsList: FC<PartyAssociationsListProps> = ({
  associations,
  listingEntries,
  handleFilterParties,
  partySelected,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [pendingDelete, setPendingDelete] = useState<NewAssociationType | null>(null);

  const {
    open,
    isCreating,
    isDeleting,
    handleOpen,
    handleClose,
    handleSubmitAssociation,
    handleDeleteAssociation,
  } = useAssociation(handleFilterParties);

  const rows = normalizeAssociations(associations);

  return (
    <>
      <PartySection
        title={t('parties:on-this-order')}
        action={
          <IconButton
            className={classes.addButton}
            aria-label={t('parties:add-association')}
            onClick={handleOpen}
            size="large"
          >
            <FontAwesomeIcon icon="plus" />
          </IconButton>
      }
      >
        {rows.length === 0 ? (
          <Typography variant="body2" className={classes.empty}>
            {t('parties:no-associations')}
          </Typography>
        ) : (
          rows.map((association) => (
            <Box key={associationKey(association)} className={classes.row}>
              <Box className={classes.rowInfo}>
                <FontAwesomeIcon
                  icon={association.parent ? faArrowUp : faArrowDown}
                  size="xs"
                  className={classes.icon}
                />
                <Box className={classes.text}>
                  <Typography variant="body2" className={classes.name}>
                    {association.party_name}
                  </Typography>
                  <Typography variant="caption" className={classes.meta}>
                    {t('parties:linked-as', { role: formatRoleName(association.linked_as) })}
                  </Typography>
                </Box>
              </Box>
              <IconButton
                className={classes.deleteButton}
                aria-label={t('parties:remove-party-aria', {
                  name: association.party_name,
                  role: formatRoleName(association.linked_as),
                })}
                onClick={() => setPendingDelete(association)}
                size="large"
              >
                <FontAwesomeIcon icon="trash" />
              </IconButton>
            </Box>
          ))
        )}
      </PartySection>

      <PartyModalAssociations
        listingEntries={listingEntries}
        open={open}
        handleClose={handleClose}
        handleSubmitAssociation={handleSubmitAssociation}
        partySelected={partySelected}
        isSubmitting={isCreating}
      />

      <ModalConfirmDelete
        open={!!pendingDelete}
        handleClose={() => setPendingDelete(null)}
        title={t('parties:remove-association-title')}
        subtitle={
        pendingDelete
          ? t('parties:remove-association-subtitle', {
            name: pendingDelete.party_name,
            linkedAs: formatRoleName(pendingDelete.linked_as),
          })
          : ''
      }
        textCancel={t('common:cancel')}
        textConfirm={t('common:remove')}
        isLoading={isDeleting}
        handleDelete={() => {
          if (!pendingDelete) return;
          handleDeleteAssociation(
            pendingDelete.party_id,
            pendingDelete.party_kind,
            () => setPendingDelete(null)
          );
        }}
      />
    </>
  );
};

export default PartyAssociationsList;
